<template>
  <div class="day-detail">
    <div class="schedule-title">
      Day {{ weekDayNumber }}
      {{ !enableOptionalButton && isOptional ? ' - Optional' : '' }}
    </div>
    <div class="schedule-actions">
      <!-- error state, only shows if delete fails -->
      <callout v-if="error" type="error">
        <div>{{ error }}</div>
      </callout>

      <!-- type fade in / out when type changes -->
      <auto-fade-help v-if="isOptionalUpdated" :title="isOptional ? 'Optional' : 'Required'" />

      <!-- toggle between required / optional only -->
      <icon-button
        v-if="enableOptionalButton"
        :icon="isOptional ? 'trip_origin' : 'radio_button_unchecked'"
        classes="-ico-only -no-container action-button"
        :label="`Set day as ${isOptional ? 'Required' : 'Optional'}`"
        :submitting="updating"
        submitting-label="Saving workout"
        :icons-outlined="false"
        aria-live="polite"
        @click-action="updateOptional"
      />

      <!-- expand / collapse -->
      <icon-button
        :icon="isExpanded ? 'expand_less' : 'expand_more'"
        classes="-ico-only -no-container action-button"
        :label="isExpanded ? 'Collapse' : 'Expand'"
        :aria-expanded="isExpanded ? 'true' : 'false'"
        :aria-controls="expandId"
        @click-action="toggleExpanded"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import IconButton from '@/components/buttons/icon-button.vue'
import AutoFadeHelp from '@/components/tool-tips/auto-fade-help.vue'
import { fbApi } from '@/utils/http-api'

/**
 * Header row for week
 * - expand / collapse action emits toggle-expanded
 * - changing is_optional emits update-action
 *
 * Assumes parent component is manging state
 */
export default defineComponent({
  name: 'DayRowHeader',
  components: { IconButton, AutoFadeHelp },
  props: {
    // 1 - 7
    weekDayNumber: {
      type: Number,
      required: true,
    },
    // 1 - last day of plan. EX: 1-28 for 4 week plan
    dayNumber: {
      type: Number,
      required: true,
    },

    // parent component handles state mgnt
    isExpanded: {
      type: Boolean,
      required: true,
    },

    // used for aria-controls to keep expand / collapse accessible
    expandId: {
      type: String,
      required: true,
    },

    // enable / disable the required / optional toggle button
    enableOptionalButton: {
      type: Boolean,
      default: false,
    },

    // TODO: define typescript type
    planDay: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['toggle-expanded', 'update-action'],

  data() {
    return {
      // days only support a boolean for optional / required
      isOptionalUpdated: false,
      updating: false,
      error: '',

      optionalMap: {
        '1': {
          name: 'Required',
          icon: 'radio_button_checked',
        },
        '0': {
          name: 'Optional',
          icon: 'radio_button_unchecked',
        },
      },
    }
  },

  computed: {
    isOptional() {
      return !!(this.planDay && this.planDay.isOptional)
    },
  },

  methods: {
    /**
     * Toggle isOptionalUpdated, then toggle off after 2 seconds
     * animation is 500ms and class addition is 1500ms via auto-fade-helper.vue
     * mount method
     */
    fadeIsOptional() {
      this.isOptionalUpdated = true
      setTimeout(() => {
        this.isOptionalUpdated = false
      }, 2000)
    },

    // pass to parent as parent manages expanded state
    toggleExpanded() {
      this.$emit('toggle-expanded', {
        dayNumber: this.dayNumber,
      })
    },

    /**
     * Update is_optional for the plan day (1 = optional, 0 = required)
     * @returns {Promise<boolean>}
     */
    async updateOptional() {
      // do not process delete twice
      if (this.updating || this.isOptionalUpdated) {
        return true
      }

      // reset state
      this.updating = true
      this.error = ''

      // next isOptional value
      const isOptional = this.planDay.isOptional ? 0 : 1

      // pass to parent component to update instantly, do not wait for XHR
      this.$emit('update-action', {
        id: this.planDay.id,
        planDay: {
          ...this.planDay,
          isOptional,
        },
      })

      // fade in is_optional value
      this.fadeIsOptional()

      try {
        const res = await fbApi.put(
          `/routines/${this.planDay.planId}/plan-days/${this.planDay.id}`,
          {
            json: {
              is_optional: isOptional,
            },
          },
        )

        if (!res || !res.body || !res.body.data) {
          throw Error('Error updating plan day')
        }
      } catch (e) {
        this.error = 'Error updating plan day'

        // on failure, emit to allow undoing the instant update
        this.$emit('update-action', {
          id: this.planDay.id,
          planDay: {
            ...this.planDay,
            isOptional: !isOptional,
          },
        })
      }

      // cancel loading
      this.updating = false
    },
  },
})
</script>
